import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  MasterAdminAuditService,
  AuditEventDTO,
  AuditFilter,
} from '../tokens/master-admin-audit.token';
import { API_PATHS } from '../config/api-paths';

/**
 * REST-backed implementation of `MasterAdminAuditService`.
 *
 * GET  /services/embedded/api/master-admin/audit
 *      → AuditEventDTO[] with X-Total-Count pagination header
 *
 * GET  /services/embedded/api/master-admin/audit/export
 *      → text/csv Blob
 *
 * Rule 07 / Rule 10 — REST-only, no mock fallback.
 * Rule 09 — callers read `X-Total-Count` via `{ observe: 'response' }`;
 *            error surfacing is done with `extractBackendErrorMessage` at the
 *            component boundary (this service does not swallow errors).
 */
@Injectable()
export class MasterAdminAuditRestService implements MasterAdminAuditService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/master-admin/audit`;
  }

  search(
    filter: AuditFilter,
    page: number,
    size: number,
  ): Observable<HttpResponse<AuditEventDTO[]>> {
    return this.http.get<AuditEventDTO[]>(this.base, {
      params: this.buildParams(filter, page, size),
      observe: 'response',
    });
  }

  exportCsv(filter: AuditFilter): Observable<Blob> {
    return this.http.get(`${this.base}/export`, {
      params: this.buildFilterParams(filter),
      responseType: 'blob',
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  private buildParams(filter: AuditFilter, page: number, size: number): HttpParams {
    let p = this.buildFilterParams(filter);
    p = p.set('page', String(page));
    p = p.set('size', String(size));
    p = p.set('sort', 'occurredAt,desc');
    return p;
  }

  private buildFilterParams(filter: AuditFilter): HttpParams {
    let p = new HttpParams();
    if (filter.eventCategory) p = p.set('eventCategory', filter.eventCategory);
    if (filter.actorLogin?.trim()) p = p.set('actorLogin', filter.actorLogin.trim());
    if (filter.from?.trim()) p = p.set('from', filter.from.trim());
    if (filter.to?.trim()) p = p.set('to', filter.to.trim());
    return p;
  }
}
